import { Link } from "react-router-dom";

import { IconLayers, IconSwords, IconTarget, IconUsers } from "@/components/Icons";
import type { FixtureChoice, OutcomeModel } from "@/lib/api/types";

import styles from "./ChoicePicker.module.css";

/**
 * Which kind of fixture, played to which rules — in one step.
 *
 * A cricket operator picks "T20". They do not pick "Match" and then "T20", even
 * though those are two separate things underneath and have to be, because kinds and
 * configs multiply: tennis is three kinds and two configs rather than six kinds.
 * Working the cross product out and offering it flat means the distinction stays
 * where it belongs — in the definition — and never reaches the person entering a
 * fixture on a Tuesday.
 *
 * A sport with one way of entering a fixture never sees this at all.
 */

const ICON: Record<OutcomeModel, typeof IconUsers> = {
  DUEL: IconSwords,
  FIELD: IconUsers,
  SERIES: IconTarget,
  COMPOSITE: IconLayers,
};

const BLURB: Record<OutcomeModel, string> = {
  DUEL: "Two sides meet and someone wins",
  FIELD: "Many start together and finish in an order",
  SERIES: "Each entrant takes a set of attempts",
  COMPOSITE: "Made up of smaller fixtures",
};

/** Units a rule's name may end in. A key ending in one of these is naming a
 *  measurement, not saying two words. */
const UNITS: Record<string, string> = {
  m: "m",
  km: "km",
  cm: "cm",
  mm: "mm",
  kg: "kg",
  lb: "lb",
  ms: "ms",
  s: "s",
  min: "min",
  ft: "ft",
};

export type Rule = { key: string; value: string };

/**
 * One rule of a config, as a label and a value.
 *
 * The version this replaces joined every rule into one string and put it in a pill,
 * which produced "50 course m · 100 distance m · LOWEST_TIME ranking rule". Three
 * separate faults: the unit was stranded as its own word, the value came before its
 * own label, and a screaming-snake constant was printed raw.
 *
 * So a trailing unit joins the number it belongs to, the label reads as a label, and a
 * constant becomes a sentence. Exported because the rules are operator-authored and
 * this formatting is the part worth pinning down in a test.
 */
export function formatRule(key: string, value: unknown): Rule {
  const words = key
    .replace(/([A-Z])/g, " $1")
    .trim()
    .split(/\s+/);
  const tail = words.length > 1 ? words[words.length - 1].toLowerCase() : "";
  const unit = UNITS[tail];

  const shown = String(value);
  const readable = /^[A-Z][A-Z0-9_]+$/.test(shown)
    ? shown.charAt(0) + shown.slice(1).toLowerCase().replace(/_/g, " ")
    : shown;

  if (unit && /^-?\d+(\.\d+)?$/.test(shown)) {
    return { key: sentence(words.slice(0, -1).join(" ")), value: `${shown}${unit}` };
  }
  return { key: sentence(words.join(" ")), value: readable };
}

function sentence(words: string): string {
  const lower = words.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function ChoicePicker({ sport, choices }: { sport: string; choices: FixtureChoice[] }) {
  return (
    <div className={styles.grid}>
      {choices.map((choice) => {
        const Icon = ICON[choice.outcome];
        const query = new URLSearchParams({ type: choice.fixtureType });
        if (choice.config) query.set("config", choice.config);
        // Four is what fits before the card starts doing the definition's job for it.
        const rules = Object.entries(choice.values)
          .slice(0, 4)
          .map(([key, value]) => formatRule(key, value));

        return (
          <Link
            key={`${choice.fixtureType}:${choice.config ?? ""}`}
            to={`/${sport}/fixtures/new?${query}`}
            className={styles.card}
          >
            <span className={styles.icon} aria-hidden>
              <Icon size={17} />
            </span>
            <span className={styles.label}>{choice.label}</span>
            <span className={styles.blurb}>{BLURB[choice.outcome]}</span>
            {rules.length > 0 && (
              <span className={styles.rules}>
                {rules.map((rule) => (
                  <span key={rule.key} className={styles.rule}>
                    {rule.key && <span className={styles.ruleKey}>{rule.key}</span>}
                    <span className={styles.ruleValue}>{rule.value}</span>
                  </span>
                ))}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
}
